import apiTamboUrl from "@/services/api";
import { ROLES, tienePermiso, esGestor, esEmpleado } from "@/config/roles";
import { getErrorMessage } from "@/services/utils/httpUtils";

// ============================================
// UTILIDADES JWT (decodificación client-side)
// ============================================

/**
 * Decodifica el payload de un JWT sin verificar la firma.
 * Se usa solo para leer la expiración del token en el cliente.
 * @param {string} token - Token JWT completo
 * @returns {object|null} - Payload decodificado o null si falla
 */
export function parseJwtPayload(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

/**
 * Verifica si un token JWT ya expiró o está por expirar.
 * @param {string} token - Token JWT
 * @param {number} margenSegundos - Margen de seguridad en segundos (default: 60)
 * @returns {boolean} - true si el token expiró o expira dentro del margen
 */
export function isTokenExpired(token, margenSegundos = 60) {
  if (!token) return true;
  const payload = parseJwtPayload(token);
  if (!payload || !payload.exp) return true;
  const ahora = Math.floor(Date.now() / 1000);
  return payload.exp - ahora <= margenSegundos;
}

// Minutos antes de la expiración para refrescar el token proactivamente
const REFRESH_ANTICIPATION_MINUTES = 5;

// ============================================
// OFFLINE LOGIN HELPERS
// ============================================

/**
 * Genera un hash simple para validar credenciales offline.
 * No es criptográfico (no necesita serlo — solo evita recrear una sesión con credenciales random).
 */
async function hashCredentials(username, password) {
  const data = new TextEncoder().encode(
    username.trim().toLowerCase() + ":" + password
  );
  if (window.crypto && window.crypto.subtle) {
    const buf = await window.crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }
  // Fallback: simple hash for older browsers
  let hash = 0;
  const str = username.trim().toLowerCase() + ":" + password;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return "fb_" + Math.abs(hash).toString(36);
}

function saveOfflineCredentialHash(hash) {
  localStorage.setItem("tambo_offline_hash", hash);
}

function getOfflineCredentialHash() {
  return localStorage.getItem("tambo_offline_hash");
}

function isNetworkError(error) {
  // No response at all = network error (offline, DNS fail, timeout)
  return (
    !error.response &&
    (error.message === "Network Error" ||
      error.code === "ERR_NETWORK" ||
      !navigator.onLine)
  );
}

function hasCachedSession() {
  return !!(
    localStorage.getItem("access_token") && localStorage.getItem("username")
  );
}

export default {
  namespaced: true,
  state: {
    userName: localStorage.getItem("username") || null,
    userFullName: localStorage.getItem("user_full_name") || null,
    userEmail: localStorage.getItem("user_email") || null,
    fotoPerfil: localStorage.getItem("foto_perfil") || null,
    userId: localStorage.getItem("user_id") || null,
    token: localStorage.getItem("access_token") || null,
    refreshToken: localStorage.getItem("refresh_token") || null,
    tokenType: localStorage.getItem("token_type") || "Bearer",
    sessionId: localStorage.getItem("session_id") || null,

    currentTamboId: localStorage.getItem("current_tambo_id") || null,
    currentTamboName: localStorage.getItem("current_tambo_name") || null,
    currentTipoSistema: (() => {
      const v = localStorage.getItem("current_tipo_sistema");
      return v && v !== "null" && v !== "undefined" ? v : null;
    })(),
    currentModoProduccion:
      localStorage.getItem("current_modo_produccion") || null,
    ventaAgroHabilitada:
      localStorage.getItem("venta_agro_habilitada") === "true",

    // ✅ NUEVO: Rol y permisos del usuario en el tambo actual
    currentRole: localStorage.getItem("current_role") || null,
    currentPermisos: JSON.parse(
      localStorage.getItem("current_permisos") || "{}"
    ),

    loading: false,
    error: null,
    isRefreshing: false,
    tokenRefreshTimer: null,
    interceptorsConfigured: false,
    isOfflineSession: false,
  },

  getters: {
    userName(state) {
      return state.userName;
    },
    userFullName(state) {
      return state.userFullName;
    },
    userEmail(state) {
      return state.userEmail;
    },
    fotoPerfil(state) {
      return state.fotoPerfil;
    },
    userId(state) {
      return state.userId;
    },
    token(state) {
      return state.token;
    },

    refreshToken(state) {
      return state.refreshToken;
    },

    isAuthenticated(state) {
      return !!state.token;
    },

    hasSelectedTambo(state) {
      return !!state.currentTamboId;
    },

    currentTamboId(state) {
      return state.currentTamboId;
    },

    currentTamboName(state) {
      return state.currentTamboName;
    },

    currentTipoSistema(state) {
      return state.currentTipoSistema;
    },

    currentModoProduccion(state) {
      return state.currentModoProduccion;
    },

    ventaAgroHabilitada(state) {
      return state.ventaAgroHabilitada;
    },
    hasVentaAgro(state) {
      return (
        !!state.ventaAgroHabilitada ||
        state.currentModoProduccion === "solo_agro"
      );
    },

    // ─── TAMBO (lechería pura) ───
    // Solo `tambo` puro: sin feedlot, sin cría propia independiente
    isModoTambo(state) {
      return (
        !state.currentModoProduccion || state.currentModoProduccion === "tambo"
      );
    },

    // ─── FEEDLOT / INVERNADA ───
    isModoFeedlot(state) {
      return state.currentModoProduccion === "invernada";
    },

    // ─── CICLO COMPLETO / MIXTOS ───
    isModoMixto(state) {
      return (
        state.currentModoProduccion === "tambo_feedlot" ||
        state.currentModoProduccion === "ciclo_completo"
      );
    },

    // ─── SOLO AGRICULTURA ───
    isModoAgricola(state) {
      return state.currentModoProduccion === "solo_agro";
    },

    // ─── GRUPOS FUNCIONALES ───

    /**
     * hasLecheria: Tiene módulo de ordeñe, leche, lotes Punta/Cola/Secas
     * = tambo, tambo_feedlot
     */
    hasLecheria(state) {
      return (
        ["tambo", "tambo_feedlot"].includes(state.currentModoProduccion) ||
        !state.currentModoProduccion
      );
    },

    /**
     * hasCria: Tiene manejo de cría propia (terneros, guachera, recría, vaquillonas)
     * = tambo, tambo_feedlot, solo_cria, ciclo_completo
     */
    hasCria(state) {
      return (
        !state.currentModoProduccion ||
        ["tambo", "tambo_feedlot", "solo_cria", "ciclo_completo"].includes(
          state.currentModoProduccion
        )
      );
    },

    /**
     * hasFeedlot: Tiene corrales y compra de animales externos
     * = invernada, tambo_feedlot, ciclo_completo
     */
    hasFeedlot(state) {
      return ["invernada", "tambo_feedlot", "ciclo_completo"].includes(
        state.currentModoProduccion
      );
    },

    /**
     * hasTamboMode: Alias de hasCria para compatibilidad con componentes existentes
     */
    hasTamboMode(state) {
      return (
        !state.currentModoProduccion ||
        ["tambo", "tambo_feedlot", "solo_cria", "ciclo_completo"].includes(
          state.currentModoProduccion
        )
      );
    },

    // ✅ NUEVO: Getters para roles y permisos
    currentRole(state) {
      return state.currentRole;
    },

    currentPermisos(state) {
      return state.currentPermisos;
    },

    /**
     * Verifica si el usuario tiene un permiso específico
     * @example: this.$store.getters['userTamboStore/hasPermission']('animales.leer')
     */
    hasPermission: (state) => (permiso) => {
      return tienePermiso(state.currentRole, permiso, state.currentPermisos);
    },

    /**
     * Verifica si el usuario es gestor/dueño del tambo actual
     */
    isGestor(state) {
      return esGestor(state.currentRole);
    },

    /**
     * Verifica si el usuario es el dueño del tambo actual
     */
    isDuenio(state) {
      return state.currentRole === ROLES.DUENIO;
    },

    /**
     * Verifica si el usuario es empleado (no dueño) del tambo actual
     */
    isEmpleado(state) {
      return esEmpleado(state.currentRole);
    },

    /**
     * Verifica si el usuario tiene rol pendiente (sin asignar)
     */
    isPendiente(state) {
      return state.currentRole === ROLES.PENDIENTE;
    },

    isOfflineSession(state) {
      return state.isOfflineSession;
    },

    loading(state) {
      return state.loading;
    },
    error(state) {
      return state.error;
    },
  },

  mutations: {
    setUserData(
      state,
      {
        username,
        user_id,
        email,
        foto_perfil,
        access_token,
        refresh_token,
        token_type,
        session_id,
        nombre_completo,
      }
    ) {
      localStorage.setItem("username", username);
      if (nombre_completo)
        localStorage.setItem("user_full_name", nombre_completo);
      localStorage.setItem("user_id", user_id || "");
      if (email) localStorage.setItem("user_email", email);
      if (foto_perfil !== undefined) {
        if (foto_perfil) {
          localStorage.setItem("foto_perfil", foto_perfil);
          state.fotoPerfil = foto_perfil;
        } else {
          localStorage.removeItem("foto_perfil");
          state.fotoPerfil = null;
        }
      }
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      localStorage.setItem("token_type", token_type || "Bearer");
      localStorage.setItem("session_id", session_id);

      state.userName = username;
      state.userFullName = nombre_completo || username;
      state.userEmail = email || state.userEmail;
      state.userId = user_id || null;
      state.token = access_token;
      state.refreshToken = refresh_token;
      state.tokenType = token_type || "Bearer";
      state.sessionId = session_id;

      console.log("✅ Datos del usuario guardados:");
    },

    setAccessToken(state, access_token) {
      localStorage.setItem("access_token", access_token);
      state.token = access_token;
      console.log("🔄 Access token actualizado");
    },

    setFotoPerfil(state, foto) {
      if (foto) {
        localStorage.setItem("foto_perfil", foto);
        state.fotoPerfil = foto;
      } else {
        localStorage.removeItem("foto_perfil");
        state.fotoPerfil = null;
      }
    },

    setCurrentTambo(
      state,
      {
        tamboId,
        tamboName,
        tipoSistema,
        modoProduccion,
        role,
        permisos,
        ventaAgroHabilitada,
      }
    ) {
      const cleanTipoSistema =
        tipoSistema && tipoSistema !== "null" && tipoSistema !== "undefined"
          ? tipoSistema
          : null;

      localStorage.setItem("current_tambo_id", tamboId);
      localStorage.setItem("current_tambo_name", tamboName || "");
      if (cleanTipoSistema) {
        localStorage.setItem("current_tipo_sistema", cleanTipoSistema);
      } else {
        localStorage.removeItem("current_tipo_sistema");
      }
      localStorage.setItem(
        "current_modo_produccion",
        modoProduccion || "tambo"
      );
      localStorage.setItem("venta_agro_habilitada", !!ventaAgroHabilitada);
      localStorage.setItem("current_role", role || null);
      localStorage.setItem("current_permisos", JSON.stringify(permisos || {}));

      state.currentTamboId = tamboId;
      state.currentTamboName = tamboName;
      state.currentTipoSistema = cleanTipoSistema;
      state.currentModoProduccion = modoProduccion || "tambo";
      state.ventaAgroHabilitada = !!ventaAgroHabilitada;
      state.currentRole = role || null;
      state.currentPermisos = permisos || {};

      console.log(
        `✅ Tambo seleccionado: ${tamboName} (ID: ${tamboId}) - Tipo: ${cleanTipoSistema} - Modo: ${modoProduccion} - Rol: ${role}`
      );
    },

    setTipoSistema(state, tipoSistema) {
      const clean =
        tipoSistema && tipoSistema !== "null" && tipoSistema !== "undefined"
          ? tipoSistema
          : null;
      if (clean) {
        localStorage.setItem("current_tipo_sistema", clean);
      } else {
        localStorage.removeItem("current_tipo_sistema");
      }
      state.currentTipoSistema = clean;
      console.log(`✅ Tipo de sistema actualizado: ${clean}`);
    },

    // ✅ NUEVO: Actualizar solo el rol y permisos
    setCurrentRole(state, { role, permisos }) {
      localStorage.setItem("current_role", role || null);
      localStorage.setItem("current_permisos", JSON.stringify(permisos || {}));

      state.currentRole = role || null;
      state.currentPermisos = permisos || {};

      console.log(`✅ Rol actualizado: ${role}`);
    },

    clearCurrentTambo(state) {
      localStorage.removeItem("current_tambo_id");
      localStorage.removeItem("current_tambo_name");
      localStorage.removeItem("current_tipo_sistema");
      localStorage.removeItem("current_modo_produccion");
      localStorage.removeItem("venta_agro_habilitada");
      localStorage.removeItem("current_role");
      localStorage.removeItem("current_permisos");

      state.currentTamboId = null;
      state.currentTamboName = null;
      state.currentTipoSistema = null;
      state.currentModoProduccion = null;
      state.ventaAgroHabilitada = false;
      state.currentRole = null;
      state.currentPermisos = {};

      console.log("🔄 Contexto de tambo limpiado");
    },

    clearUserData(state) {
      state.userName = null;
      state.userFullName = null;
      state.userEmail = null;
      state.userId = null;
      state.token = null;
      state.refreshToken = null;
      state.isRefreshing = false;
      state.tokenType = null;
      state.sessionId = null;
      state.isOfflineSession = false;
      state.fotoPerfil = null;
      // ✅ NO limpiar el tambo - mantenerlo como preferencia del dispositivo
      // El tambo se validará en el próximo login con syncTamboAfterLogin
      state.currentRole = null;
      state.currentPermisos = {};

      // Limpiar localStorage - datos de autenticación
      localStorage.removeItem("username");
      localStorage.removeItem("user_full_name");
      localStorage.removeItem("user_id");
      localStorage.removeItem("user_email");
      localStorage.removeItem("foto_perfil");
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("token_type");
      localStorage.removeItem("session_id");
      localStorage.removeItem("tambo_credentials");
      // ✅ Mantener current_tambo_id y current_tambo_name
      // Se validarán en syncTamboAfterLogin al hacer login
      localStorage.removeItem("current_role");
      localStorage.removeItem("current_permisos");
    },

    setLoading(state, value) {
      state.loading = value;
    },

    setError(state, error) {
      state.error = error;
    },

    setIsRefreshing(state, value) {
      state.isRefreshing = value;
    },

    setTokenRefreshTimer(state, timerId) {
      // Limpiar timer anterior si existe
      if (state.tokenRefreshTimer) {
        clearTimeout(state.tokenRefreshTimer);
      }
      state.tokenRefreshTimer = timerId;
    },

    setInterceptorsConfigured(state, value) {
      state.interceptorsConfigured = value;
    },

    setOfflineSession(state, value) {
      state.isOfflineSession = value;
    },
  },

  actions: {
    async login({ commit, dispatch }, credentials) {
      commit("setLoading", true);
      commit("setError", null);

      try {
        // Enviar credenciales en el body (JSON) según LoginRequest del backend
        const response = await apiTamboUrl.post("/login", {
          username: credentials.username,
          password: credentials.password,
        });

        console.log("Respuesta de login:", response);

        // Guardar datos del usuario
        const nombreStr = response.data.user
          ? response.data.user.nombre || ""
          : "";
        const apellidoStr = response.data.user
          ? response.data.user.apellido || ""
          : "";
        const fullName =
          `${nombreStr} ${apellidoStr}`.trim() || credentials.username;

        const userData = {
          username: credentials.username,
          user_id: response.data.user?.id || response.data.user_id,
          email: response.data.user?.email || response.data.email || null,
          foto_perfil: response.data.user?.foto_perfil || null,
          access_token: response.data.access_token,
          refresh_token: response.data.refresh_token,
          token_type: response.data.token_type,
          session_id:
            response.data.user?.session_id || response.data.session_id,
          nombre_completo: fullName,
        };

        commit("setUserData", userData);
        console.log("Datos del usuario guardados en Vuex:", userData);
        dispatch("setupInterceptors");

        // Iniciar el timer de refresh proactivo
        dispatch("startTokenRefreshTimer");

        // Guardar hash de credenciales para login offline futuro
        try {
          const credHash = await hashCredentials(
            credentials.username,
            credentials.password
          );
          saveOfflineCredentialHash(credHash);
        } catch (e) {
          console.warn("No se pudo guardar hash offline:", e);
        }

        return true;
      } catch (error) {
        console.error("Error en login:", error);

        // ── LOGIN OFFLINE ──
        // Si no hay red y hay sesión cacheada, intentar login offline
        if (isNetworkError(error) && hasCachedSession()) {
          try {
            const credHash = await hashCredentials(
              credentials.username,
              credentials.password
            );
            const storedHash = getOfflineCredentialHash();

            if (storedHash && credHash === storedHash) {
              console.log(
                "Login offline: credenciales válidas, restaurando sesión cacheada"
              );

              // Restaurar datos desde localStorage (ya guardados por el último login online)
              const cachedData = {
                username: localStorage.getItem("username"),
                user_id: parseInt(localStorage.getItem("user_id"), 10),
                email: localStorage.getItem("email") || null,
                access_token: localStorage.getItem("access_token"),
                refresh_token: localStorage.getItem("refresh_token"),
                token_type: "bearer",
                session_id: localStorage.getItem("session_id") || null,
              };

              commit("setUserData", cachedData);
              dispatch("setupInterceptors");

              // Restaurar tambo cacheado
              const savedTamboId = localStorage.getItem("current_tambo_id");
              const savedTamboName = localStorage.getItem("current_tambo_name");
              const savedTipoSistema = localStorage.getItem(
                "current_tipo_sistema"
              );
              const savedModo = localStorage.getItem("current_modo_produccion");
              const savedRole = localStorage.getItem("current_role");
              const savedPermisos = JSON.parse(
                localStorage.getItem("current_permisos") || "{}"
              );
              if (savedTamboId) {
                commit("setCurrentTambo", {
                  tamboId: parseInt(savedTamboId, 10),
                  tamboName: savedTamboName || "",
                  tipoSistema: savedTipoSistema,
                  modoProduccion: savedModo,
                  role: savedRole,
                  permisos: savedPermisos,
                });
              }

              // Marcar como sesión offline
              commit("setOfflineSession", true);

              return true;
            } else {
              commit(
                "setError",
                "Sin conexión. Las credenciales no coinciden con la última sesión."
              );
              return false;
            }
          } catch (offlineErr) {
            console.error("Error en login offline:", offlineErr);
          }
        }

        const errorMsg = msgApiError(error, "Error al iniciar sesión");
        commit("setError", errorMsg);
        return false;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * Después del login, sincroniza el tambo actual con el backend
     * Devuelve { hasTambo: boolean, tamboId, tamboName } para que el caller sepa a dónde redirigir
     */
    async syncTamboAfterLogin({ commit, dispatch, state }) {
      try {
        // 1. Obtener la lista de tambos del usuario
        const tambosResponse = await apiTamboUrl.get("/mis-tambos/");
        const tambos = tambosResponse.data || [];

        console.log("🔍 syncTamboAfterLogin - Tambos del usuario:", tambos);

        if (tambos.length === 0) {
          console.log("⚠️ Usuario sin tambos asignados");
          commit("clearCurrentTambo");
          return { hasTambo: false, tambos: [] };
        }

        // Caso 1: Exactamente 1 estancia asociada -> Ingreso directo sin tener que seleccionarla
        if (tambos.length === 1) {
          const soloTambo = tambos[0];
          console.log(
            `🎯 Única estancia asociada: ${soloTambo.nombre} (ID: ${soloTambo.id}). Ingresando directamente...`
          );

          const success = await dispatch("changeTambo", {
            tamboId: soloTambo.id,
            tamboName: soloTambo.nombre,
            tipoSistema: soloTambo.tipo_sistema,
            modoProduccion: soloTambo.modo_produccion || "tambo",
          });

          if (success) {
            if (state.currentRole === "pendiente") {
              console.log("⚠️ Usuario con rol pendiente en su única estancia");
              return { hasTambo: false, isPendiente: true, tambos };
            }

            return {
              hasTambo: true,
              tamboId: soloTambo.id,
              tamboName: soloTambo.nombre,
              modoProduccion: soloTambo.modo_produccion || "tambo",
              tambos,
            };
          }
        }

        // Caso 2: Más de 1 estancia asociada -> El usuario debe elegirla desde la pantalla "Mis Estancias"
        console.log(
          `📋 Usuario con ${tambos.length} estancias asociadas. Debe elegir desde la pantalla de selección.`
        );
        commit("clearCurrentTambo");
        return { hasTambo: false, tambos };
      } catch (error) {
        console.error("Error sincronizando tambo:", error);
        return { hasTambo: false, tambos: [] };
      }
    },

    async changeTambo(
      { commit, dispatch, state },
      { tamboId, tamboName, tipoSistema, modoProduccion }
    ) {
      commit("setLoading", true);

      try {
        // 1. Guardar el nuevo tambo en el backend
        const response = await apiTamboUrl.post("/usuarios/tambo-actual/", {
          tambo_id: tamboId,
        });

        if (response.status === 200 || response.status === 201) {
          // 2. Obtener el rol del usuario en este tambo
          let role = null;
          let permisos = {};

          try {
            const participantesResponse = await apiTamboUrl.get(
              `/tambos/${tamboId}/participantes/`
            );

            if (participantesResponse.data) {
              // Buscar el participante actual por user_id o username
              const miParticipacion = participantesResponse.data.find(
                (p) =>
                  p.user_id === parseInt(state.userId) ||
                  p.username === state.userName
              );

              if (miParticipacion) {
                role = miParticipacion.role;
                permisos = miParticipacion.permisos || {};
                console.log(`📋 Rol obtenido: ${role}`);
              }
            }
          } catch (err) {
            console.warn("⚠️ No se pudo obtener el rol del usuario:", err);
          }

          // 3. Actualizar el tambo y rol en el estado local
          commit("setCurrentTambo", {
            tamboId,
            tamboName,
            tipoSistema,
            modoProduccion: modoProduccion || "tambo",
            role,
            permisos,
          });

          // 4. ✅ CRUCIAL: Limpiar todos los stores de datos
          await dispatch("clearAllStoresData", null, { root: true });

          console.log(`🔄 Cambiado a tambo: ${tamboName} - Rol: ${role}`);
          return true;
        }
      } catch (error) {
        console.error("Error al cambiar de tambo:", error);
        commit("setError", "Error al cambiar de tambo");
        return false;
      } finally {
        commit("setLoading", false);
      }
    },

    // ✅ Refrescar el access token usando el refresh token
    async refreshAccessToken({ commit, state, dispatch }) {
      // Evitar múltiples refreshes simultáneos
      if (state.isRefreshing) {
        console.log("⏳ Ya hay un refresh en progreso...");
        return false;
      }

      if (!state.refreshToken) {
        console.error("❌ No hay refresh token disponible");
        dispatch("logout");
        return false;
      }

      commit("setIsRefreshing", true);

      try {
        console.log("🔄 Refrescando access token...");

        const response = await apiTamboUrl.post("/refresh", {
          refresh_token: state.refreshToken,
        });

        if (response.data.access_token) {
          commit("setAccessToken", response.data.access_token);
          console.log("✅ Access token refrescado exitosamente");

          // Reprogramar el timer de refresh con el nuevo token
          dispatch("startTokenRefreshTimer");

          return true;
        } else {
          throw new Error("No se recibió nuevo access token");
        }
      } catch (error) {
        console.error("❌ Error al refrescar token:", error);
        alert("Tu sesión ha expirado. Por favor, inicia sesión nuevamente.");
        dispatch("logout");
        return false;
      } finally {
        commit("setIsRefreshing", false);
      }
    },

    /**
     * Programa un timer para refrescar el access token proactivamente
     * antes de que expire, evitando interrupciones en la sesión del usuario.
     */
    startTokenRefreshTimer({ commit, state, dispatch }) {
      // Limpiar timer previo
      dispatch("stopTokenRefreshTimer");

      const token = state.token || localStorage.getItem("access_token");
      if (!token) return;

      const payload = parseJwtPayload(token);
      if (!payload || !payload.exp) return;

      const ahora = Math.floor(Date.now() / 1000);
      const segundosHastaExpiracion = payload.exp - ahora;

      // Si el token ya expiró, refrescar inmediatamente
      if (segundosHastaExpiracion <= 0) {
        console.log("⚠️ Token ya expirado, refrescando inmediatamente...");
        dispatch("refreshAccessToken");
        return;
      }

      // Refrescar N minutos antes de que expire
      const segundosParaRefresh = Math.max(
        segundosHastaExpiracion - REFRESH_ANTICIPATION_MINUTES * 60,
        30 // Mínimo 30 segundos
      );

      console.log(
        `⏰ Token refresh programado en ${Math.round(
          segundosParaRefresh / 60
        )} minutos`
      );

      const timerId = setTimeout(() => {
        console.log("⏰ Ejecutando refresh proactivo del token...");
        dispatch("refreshAccessToken");
      }, segundosParaRefresh * 1000);

      commit("setTokenRefreshTimer", timerId);
    },

    /**
     * Detiene el timer de refresh proactivo
     */
    stopTokenRefreshTimer({ commit, state }) {
      if (state.tokenRefreshTimer) {
        clearTimeout(state.tokenRefreshTimer);
        commit("setTokenRefreshTimer", null);
      }
    },

    logout({ commit, dispatch }) {
      // Detener el timer de refresh proactivo
      dispatch("stopTokenRefreshTimer");

      commit("clearUserData");
      commit("clearCurrentTambo");
      dispatch("criaEficienciaStore/reiniciar", null, { root: true });
      window.dispatchEvent(new CustomEvent("auth:expired"));
    },

    async updateProfilePhoto({ commit }, fotoBase64) {
      try {
        const response = await apiTamboUrl.patch("/perfil", {
          foto_perfil: fotoBase64 || "",
        });
        const fotoGuardada = response.data.foto_perfil || null;
        commit("setFotoPerfil", fotoGuardada);
        return { success: true, foto_perfil: fotoGuardada };
      } catch (error) {
        console.error("Error al actualizar foto de perfil:", error);
        throw error;
      }
    },

    setupInterceptors({ state, dispatch, commit }) {
      // Evitar configurar interceptores múltiples veces
      if (state.interceptorsConfigured) {
        console.log("✓ Interceptores ya configurados");
        return;
      }

      console.log(
        "Configurando interceptores con token:",
        state.token ? "✓" : "✗"
      );

      // Limpiar interceptores existentes
      apiTamboUrl.interceptors.request.handlers = [];
      apiTamboUrl.interceptors.response.handlers = [];

      // Variable para manejar la cola de refresh
      let refreshPromise = null;

      // Configurar interceptor de petición - OBTENER TOKEN DEL LOCALSTORAGE SIEMPRE
      apiTamboUrl.interceptors.request.use((config) => {
        // Obtener el token más reciente del localStorage (no del state capturado)
        const currentToken = localStorage.getItem("access_token");
        const tokenType = localStorage.getItem("token_type") || "Bearer";

        if (currentToken) {
          config.headers.Authorization = `${tokenType} ${currentToken}`;
        }
        const currentTamboId = localStorage.getItem("current_tambo_id");
        if (currentTamboId) {
          config.headers = config.headers || {};
          config.headers["X-Tambo-Id"] = currentTamboId;
        }
        return config;
      });

      // ✅ Interceptor de respuesta con manejo de refresh token mejorado
      apiTamboUrl.interceptors.response.use(
        (response) => response,
        async (error) => {
          const originalRequest = error.config;

          // No intentar refresh en endpoints de autenticación
          const isAuthEndpoint =
            originalRequest.url?.includes("/login") ||
            originalRequest.url?.includes("/refresh") ||
            originalRequest.url?.includes("/register");

          // Si es 401, no es un endpoint de auth, y no hemos reintentado
          if (
            error.response?.status === 401 &&
            !isAuthEndpoint &&
            !originalRequest._retry
          ) {
            originalRequest._retry = true;

            const refreshToken = localStorage.getItem("refresh_token");

            if (!refreshToken) {
              console.log("❌ No hay refresh token, cerrando sesión");
              dispatch("logout");
              return Promise.reject(error);
            }

            // Si ya hay un refresh en progreso, esperar a que termine
            if (refreshPromise) {
              console.log("⏳ Esperando refresh en progreso...");
              try {
                await refreshPromise;
                // Reintentar con el nuevo token
                const newToken = localStorage.getItem("access_token");
                const tokenType =
                  localStorage.getItem("token_type") || "Bearer";
                originalRequest.headers.Authorization = `${tokenType} ${newToken}`;
                return apiTamboUrl(originalRequest);
              } catch (refreshError) {
                return Promise.reject(refreshError);
              }
            }

            console.log("🔑 Token expirado, intentando refrescar...");

            // Crear una promesa para el refresh
            refreshPromise = (async () => {
              try {
                const response = await apiTamboUrl.post("/refresh", {
                  refresh_token: refreshToken,
                });

                if (response.data.access_token) {
                  commit("setAccessToken", response.data.access_token);
                  console.log("✅ Access token refrescado exitosamente");

                  // Reprogramar timer tras refresh exitoso en interceptor
                  dispatch("startTokenRefreshTimer");

                  return true;
                }
                throw new Error("No se recibió nuevo access token");
              } catch (refreshError) {
                console.error("❌ Error al refrescar token:", refreshError);
                dispatch("logout");
                throw refreshError;
              } finally {
                refreshPromise = null;
              }
            })();

            try {
              await refreshPromise;
              // Reintentar la petición original con el nuevo token
              const newToken = localStorage.getItem("access_token");
              const tokenType = localStorage.getItem("token_type") || "Bearer";
              originalRequest.headers.Authorization = `${tokenType} ${newToken}`;
              return apiTamboUrl(originalRequest);
            } catch (refreshError) {
              return Promise.reject(refreshError);
            }
          }

          return Promise.reject(error);
        }
      );

      commit("setInterceptorsConfigured", true);
    },

    async autoLogin({ state, dispatch, commit }) {
      if (!state.token) {
        console.log("⚠️ No hay token guardado");
        return false;
      }

      // Configurar interceptores primero
      dispatch("setupInterceptors");

      // Verificar si el access token ya expiró
      const tokenExpirado = isTokenExpired(state.token, 0);

      if (tokenExpirado) {
        console.log("⚠️ Access token expirado, intentando refrescar...");

        // Verificar si el refresh token también expiró
        if (!state.refreshToken || isTokenExpired(state.refreshToken, 0)) {
          console.log("❌ Refresh token también expirado, requiere login");
          commit("clearUserData");
          return false;
        }

        // Intentar refrescar el token
        const refreshOk = await dispatch("refreshAccessToken");
        if (!refreshOk) {
          return false;
        }
      } else {
        // Token válido, programar el refresh proactivo
        dispatch("startTokenRefreshTimer");
      }

      // Sincronizar tambo activo con el backend si hay tambo guardado y conexión
      const savedTamboId = localStorage.getItem("current_tambo_id");
      if (savedTamboId && navigator.onLine) {
        try {
          await apiTamboUrl.post("/usuarios/tambo-actual/", {
            tambo_id: parseInt(savedTamboId, 10),
          });
        } catch (syncErr) {
          console.warn("No se pudo sincronizar tambo actual en backend:", syncErr);
        }
      }

      console.log("✅ Sesión restaurada desde localStorage", {
        hasToken: !!state.token,
        hasTambo: !!state.currentTamboId,
        tamboName: state.currentTamboName,
        role: state.currentRole,
      });

      return true;
    },

    async register({ commit }, userData) {
      commit("setLoading", true);
      commit("setError", null);

      try {
        // Enviar todos los datos de registro
        const payload = {
          username: userData.username,
          password: userData.password,
          nombre: userData.nombre,
          apellido: userData.apellido,
          email: userData.email,
          es_duenio: userData.es_duenio,
        };

        // Agregar campos específicos para dueños
        if (userData.es_duenio) {
          payload.tambo_nombre = userData.tambo_nombre;
          payload.modo_produccion = userData.modo_produccion;
          payload.token_duenio = userData.token_duenio;
          if (
            ["tambo", "tambo_feedlot", "solo_cria", "ciclo_completo"].includes(
              userData.modo_produccion
            )
          ) {
            payload.tipo_sistema = userData.tipo_sistema;
          }
        }

        const response = await apiTamboUrl.post("/register/", payload);

        console.log("Respuesta de registro:", response);

        if (response.status === 200 || response.status === 201) {
          return true;
        } else {
          commit(
            "setError",
            "Error en el registro. Por favor, inténtalo de nuevo."
          );
          return false;
        }
      } catch (error) {
        console.error("Error en registro:", error);
        const errorMsg = msgApiError(error, "Error al registrarse");
        commit("setError", errorMsg);
        return false;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * Verificar una invitación por token
     * @param {string} token - Token de la invitación
     * @returns {object} - Datos de la invitación (tambo_nombre, role, etc.)
     */
    async verificarInvitacion(_, token) {
      try {
        const response = await apiTamboUrl.get(
          `/invitaciones/verificar/${token}`
        );
        if (response.status === 200) {
          console.log("✅ Invitación verificada:", response.data);
          return response.data;
        }
      } catch (error) {
        console.error("❌ Error verificando invitación:", error);
        throw error;
      }
    },

    /**
     * Aceptar una invitación por token
     * @param {string} token - Token de la invitación
     */
    async aceptarInvitacion({ commit, dispatch }, token) {
      commit("setLoading", true);
      try {
        const response = await apiTamboUrl.post("/invitaciones/aceptar/", {
          token,
        });
        if (response.status === 200 || response.status === 201) {
          console.log("✅ Invitación aceptada:", response.data);
          // Sincronizar tambo después de aceptar
          await dispatch("syncTamboAfterLogin");
          return response.data;
        }
      } catch (error) {
        console.error("❌ Error aceptando invitación:", error);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },
  },
};

// Función helper para manejar errores de API
function msgApiError(error, defaultMessage = "Error inesperado") {
  // Manejo especial para login: si 401, credenciales incorrectas
  if (error?.response?.status === 401) {
    return "Usuario o contraseña incorrectos";
  }
  return getErrorMessage(error, defaultMessage);
}
